import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FONT, COLOURS } from '../constants/theme';
import { COUPLE, CEREMONY, TIMELINE, NAV_ITEMS } from '../constants/weddingData';
import StatusBar     from '../components/ui/StatusBar';
import Dialog        from '../components/ui/Dialog';
import SectionHeader from '../components/ui/SectionHeader';
import PixelCard     from '../components/ui/PixelCard';
import PixelWindow   from '../components/ui/PixelWindow';
import BottomNav     from '../components/ui/BottomNav';
import TimelineRow   from '../components/ui/TimelineRow';
import RsvpForm      from '../components/forms/RsvpForm';

const SITE = CEREMONY.japan;

/**
 * JapanSite — the Osaka ceremony sub-website.
 *
 * Sub-pages: Home | Story | Details | RSVP
 * Navigating to the 'dock' key calls onBack() to return to DockScene.
 *
 * The home-page pixel scene depicts Nanten-en Inn at golden dusk:
 *   - Meiji-era ryokan with pagoda roofs and shoji windows
 *   - Stone torii gate, hanging lanterns, bamboo grove
 *   - Koi pond, autumn maple trees
 *   - Pixel-art couple in traditional wedding attire
 *
 * @param {object}   props
 * @param {function} props.onBack - Callback to return to the dock world-select.
 */
function JapanSite({ onBack }) {
  const [page, setPage] = useState('home');

  const handleNav = (key) => {
    if (key === 'dock') { onBack(); return; }
    setPage(key);
  };

  return (
    <div
      className="fadeIn"
      style={{ fontFamily: FONT, background: COLOURS.cream, minHeight: '100vh' }}
    >
      <StatusBar left="⛩ OSAKA" mid="JAPAN CEREMONY" right="♥♥♥♥♥" />

      {/* ════════════ HOME PAGE ════════════ */}
      {page === 'home' && (
        <>
          {/* Pixel art Nanten-en ryokan scene */}
          <svg
            width="100%"
            viewBox="0 0 380 268"
            style={{ display: 'block' }}
            role="img"
            aria-label="Nanten-en ryokan at golden dusk with torii gate, koi pond, bamboo, and maple trees"
          >
            {/* ── SKY — warm autumn dusk ── */}
            <rect x="0"  y="0"   width="380" height="268" fill="#f5c890" />
            <rect x="0"  y="0"   width="380" height="52"  fill="#e8904a" />
            <rect x="0"  y="52"  width="380" height="82"  fill="#f5aa68" />
            <rect x="0"  y="134" width="380" height="50"  fill="#f0d8b0" />

            {/* Setting sun */}
            <rect x="20" y="14"  width="40"  height="40"  fill="#FFD080" />
            <rect x="16" y="22"  width="4"   height="24"  fill="#FFD080" />
            <rect x="60" y="22"  width="4"   height="24"  fill="#FFD080" />
            <rect x="24" y="10"  width="32"  height="4"   fill="#FFD080" />
            <rect x="24" y="54"  width="32"  height="4"   fill="#FFD080" />

            {/* Clouds */}
            {[[90,16],[238,12]].map(([cx,cy], i) => (
              <g key={i}>
                <rect x={cx}   y={cy}   width="64" height="14" fill="#fff0e0" opacity="0.8" />
                <rect x={cx-6} y={cy+8} width="76" height="14" fill="#fff0e0" opacity="0.8" />
              </g>
            ))}

            {/* ── HILLS — soft autumn colours ── */}
            <ellipse cx="80"  cy="150" rx="92"  ry="40" fill="#d4a870" />
            <ellipse cx="200" cy="156" rx="122" ry="38" fill="#c89858" />
            <ellipse cx="330" cy="150" rx="82"  ry="36" fill="#d4a870" />

            {/* ── GROUND ── */}
            <rect x="0" y="175" width="380" height="93"  fill="#e8c888" />
            <rect x="0" y="173" width="380" height="5"   fill="#c9a050" />
            <rect x="0" y="173" width="380" height="12"  fill="#a8c870" />
            <rect x="0" y="179" width="380" height="6"   fill="#90b058" />
            <rect x="0" y="240" width="380" height="28"  fill="#c49040" />

            {/* ── RYOKAN — Nanten-en Inn ── */}
            {/* Main building body */}
            <rect x="110" y="138" width="160" height="92" fill="#e8d8b8" />
            {/* Horizontal panel lines */}
            {[148,161,174,187,200,213,225].map((py) => (
              <rect key={py} x="110" y={py} width="160" height="2" fill="#d4c098" opacity="0.52" />
            ))}
            {/* Main eave beam */}
            <rect x="102" y="162" width="176" height="10" fill="#8a5a28" />
            {/* Primary pagoda roof */}
            <polygon points="94,162 190,112 286,162"  fill="#6b3820" />
            <polygon points="97,162 190,115 283,162"  fill="#8a4a28" />
            <rect x="94"  y="160" width="192" height="5"  fill="#5a2810" />
            {/* Secondary (upper) roof tier */}
            <polygon points="132,138 190,110 248,138" fill="#8a4a28" />
            <polygon points="134,138 190,112 246,138" fill="#a05a38" />
            <rect x="132" y="136" width="116" height="4"  fill="#6b3820" />
            {/* Roof finial */}
            <rect x="184" y="96"  width="12"  height="14" fill="#c9843c" />
            <rect x="182" y="94"  width="16"  height="4"  fill="#b85c38" />

            {/* Shoji (paper screen) windows */}
            {[[124,140],[222,140]].map(([wx,wy]) => (
              <g key={wx}>
                <rect x={wx}    y={wy}    width="34" height="25" fill="#fff8e8" />
                <rect x={wx}    y={wy}    width="34" height="3"  fill="#8a5a28" />
                <rect x={wx}    y={wy}    width="3"  height="25" fill="#8a5a28" />
                <rect x={wx+31} y={wy}    width="3"  height="25" fill="#8a5a28" />
                <rect x={wx}    y={wy+22} width="34" height="3"  fill="#8a5a28" />
                <rect x={wx+10} y={wy}    width="2"  height="25" fill="#d4c098" opacity="0.55" />
                <rect x={wx+20} y={wy}    width="2"  height="25" fill="#d4c098" opacity="0.55" />
                <rect x={wx}    y={wy+12} width="34" height="2"  fill="#d4c098" opacity="0.55" />
                {/* Warm candlelight glow */}
                <rect x={wx+3}  y={wy+3}  width="8"  height="8"  fill="#ffe0a0" opacity="0.7" />
              </g>
            ))}

            {/* Main entrance door */}
            <rect x="172" y="190" width="36"  height="40" fill="#d4b880" />
            <rect x="172" y="190" width="18"  height="40" fill="#c9a870" />
            <rect x="172" y="190" width="3"   height="40" fill="#8a5a28" />
            <rect x="205" y="190" width="3"   height="40" fill="#8a5a28" />
            <rect x="188" y="190" width="3"   height="40" fill="#8a5a28" />
            <rect x="172" y="190" width="36"  height="3"  fill="#8a5a28" />
            <rect x="172" y="204" width="36"  height="2"  fill="#b89058" opacity="0.5" />
            <rect x="172" y="218" width="36"  height="2"  fill="#b89058" opacity="0.5" />
            <rect x="201" y="208" width="4"   height="4"  fill="#c9843c" />  {/* door handle */}

            {/* Porch beam */}
            <rect x="110" y="228" width="160" height="7"  fill="#c4a878" />
            {/* Corner pillars */}
            <rect x="118" y="172" width="7"   height="58" fill="#8a5a28" />
            <rect x="255" y="172" width="7"   height="58" fill="#8a5a28" />

            {/* ── TORII GATE ── */}
            <rect x="22"  y="180" width="6"   height="44" fill="#c85a28" />  {/* left post */}
            <rect x="42"  y="180" width="6"   height="44" fill="#c85a28" />  {/* right post */}
            <rect x="16"  y="178" width="56"  height="6"  fill="#c85a28" />  {/* top rail */}
            <rect x="12"  y="184" width="64"  height="4"  fill="#e07038" />  {/* kasagi highlight */}

            {/* ── STONE LANTERNS ── */}
            {[[72,218],[298,218]].map(([lx,ly]) => (
              <g key={lx}>
                <rect x={lx}   y={ly}    width="8"  height="18" fill="#a09080" />   {/* post */}
                <rect x={lx-4} y={ly-6}  width="16" height="12" fill="#b0a090" />   {/* body */}
                <rect x={lx-2} y={ly-4}  width="12" height="8"  fill="#fff0d0" opacity="0.8" /> {/* glow */}
                <rect x={lx-6} y={ly-8}  width="20" height="4"  fill="#909080" />   {/* cap */}
                <rect x={lx-2} y={ly+18} width="12" height="4"  fill="#a09080" />   {/* base */}
                <rect x={lx-8} y={ly+22} width="24" height="3"  fill="#909080" />   {/* plinth */}
                {/* Ambient lantern glow */}
                <rect x={lx-8} y={ly-10} width="24" height="22" fill="#ffd080" opacity="0.14" />
              </g>
            ))}

            {/* ── BAMBOO GROVE — right edge ── */}
            {[320,330,340,352,364,376].map((bx, i) => (
              <g key={bx}>
                <rect x={bx} y="148" width="4" height="85"
                  fill={i % 2 === 0 ? '#8ab048' : '#9abf52'}
                />
                {/* Node rings on bamboo */}
                {[164,182,200,218].map((by) => (
                  <rect key={by} x={bx - 2} y={by} width="8" height="3"
                    fill={i % 2 === 0 ? '#6a9030' : '#7aaa38'}
                  />
                ))}
              </g>
            ))}

            {/* ── MAPLE TREES — flanking the building ── */}
            {/* Left tree cluster */}
            <rect x="54"  y="198" width="10"  height="44" fill="#8a5a28" />
            <rect x="18"  y="164" width="56"  height="46" fill="#c8481a" opacity="0.9" />
            <rect x="12"  y="172" width="30"  height="30" fill="#d85a20" opacity="0.85"/>
            <rect x="46"  y="158" width="40"  height="36" fill="#b83a10" opacity="0.9" />
            <rect x="22"  y="196" width="40"  height="18" fill="#d85a20" opacity="0.8" />
            {/* Right tree cluster */}
            <rect x="304" y="194" width="10"  height="48" fill="#8a5a28" />
            <rect x="274" y="160" width="56"  height="46" fill="#c8481a" opacity="0.9" />
            <rect x="288" y="154" width="40"  height="38" fill="#b83a10" opacity="0.9" />
            <rect x="276" y="196" width="34"  height="18" fill="#d85a20" opacity="0.75"/>

            {/* ── KOI POND ── */}
            <rect x="62"  y="244" width="80"  height="22" fill="#7ab8d0" />
            <rect x="62"  y="244" width="80"  height="4"  fill="#5a98b0" />
            {/* Lily pads */}
            <rect x="70"  y="252" width="12"  height="8"  fill="#5a9a38" />
            <rect x="102" y="248" width="14"  height="8"  fill="#6aaa48" />
            {/* Koi fish */}
            <rect x="80"  y="256" width="10"  height="5"  fill="#e85828" />   {/* red koi */}
            <rect x="112" y="252" width="10"  height="5"  fill="#ffffff" opacity="0.9" /> {/* white koi */}

            {/* ── PIXEL COUPLE in traditional attire ── */}
            {/* Groom — haori / hakama */}
            <rect x="148" y="228" width="14"  height="24" fill="#4a5870" />  {/* hakama */}
            <rect x="148" y="218" width="20"  height="12" fill="#4a5870" />  {/* haori */}
            <rect x="151" y="219" width="14"  height="10" fill="#e8d8b8" />  {/* inner collar */}
            <rect x="149" y="214" width="18"  height="5"  fill="#c4a882" />  {/* neck */}
            <rect x="150" y="198" width="16"  height="18" fill="#c4a882" />  {/* face */}
            <rect x="148" y="196" width="20"  height="5"  fill="#2a1808" />  {/* hair */}
            {/* Bride — uchikake / shiromuku */}
            <rect x="188" y="224" width="30"  height="14" fill="#f0e8d0" />  {/* outer kimono */}
            <rect x="192" y="212" width="22"  height="14" fill="#fff8f0" />  {/* inner kimono */}
            <rect x="190" y="208" width="26"  height="6"  fill="#c8481a" />  {/* obi sash */}
            <rect x="192" y="202" width="22"  height="8"  fill="#fff8f0" />  {/* bodice */}
            <rect x="193" y="196" width="20"  height="7"  fill="#f5d4c8" />  {/* neck */}
            <rect x="192" y="180" width="22"  height="18" fill="#f5d4c8" />  {/* face */}
            <rect x="190" y="177" width="26"  height="7"  fill="#e890a8" />  {/* kanzashi hair pin */}
            {/* Pixel heart between couple */}
            {[
              [174,208,3,3],[180,208,3,3],
              [172,211,5,3],[180,211,5,3],
              [170,214,17,3],
              [172,217,13,3],
              [174,220,9,3],
              [176,223,5,2],
            ].map(([x,y,w,h], i) => (
              <rect key={i} x={x} y={y} width={w} height={h} fill="#c85a38" />
            ))}

            {/* Falling leaves over scene */}
            {[[100,142],[134,128],[252,136],[284,122],[60,150],[318,142]].map(([lx,ly], i) => (
              <rect key={i} x={lx} y={ly} width="7" height="6"
                fill={['#c8481a','#d85a20','#e87030'][i % 3]} opacity="0.85"
                style={{ animation: `leaf ${4.5 + i * 0.4}s ${i * 0.55}s ease-in infinite` }}
              />
            ))}

            {/* Fairy sparkles */}
            {[[88,192,0],[294,196,0.5],[150,177,0.3],[234,175,0.8]].map(([sx,sy,sd], i) => (
              <g key={i} className="sp" style={{ animationDelay: `${sd}s` }}>
                <rect x={sx}   y={sy}   width="4" height="4" fill="#ffe0a0" opacity="0.9" />
                <rect x={sx-2} y={sy+2} width="8" height="2" fill="#ffe0a0" opacity="0.4" />
                <rect x={sx+1} y={sy-2} width="2" height="8" fill="#ffe0a0" opacity="0.4" />
              </g>
            ))}

            {/* ── TITLE CARD — high above scene ── */}
            <rect x="112" y="4"   width="196" height="80" fill="#fff8f0" opacity="0.97" />
            <rect x="112" y="4"   width="196" height="80" fill="none" stroke="#c9843c" strokeWidth="3" />
            {[[110,2],[300,2],[110,76],[300,76]].map(([cx,cy], i) => (
              <rect key={i} x={cx} y={cy} width="10" height="10" fill="#c9843c" />
            ))}
            <text x="210" y="22"  textAnchor="middle" fontFamily={FONT} fontSize="5"  fill="#8a6848">
              ⛩ The Wedding of
            </text>
            <text x="210" y="44"  textAnchor="middle" fontFamily={FONT} fontSize="14" fill="#b85c38">
              {COUPLE.bride}
            </text>
            <text x="210" y="62"  textAnchor="middle" fontFamily={FONT} fontSize="14" fill="#b85c38">
              &amp; {COUPLE.groom}
            </text>
            <text x="210" y="78"  textAnchor="middle" fontFamily={FONT} fontSize="5"  fill="#8a6848">
              31 Oct 2026 · Nanten-en · Osaka
            </text>
          </svg>

          {/* Info cards */}
          <div style={{ padding: '10px 12px' }}>
            <SectionHeader>OSAKA CELEBRATION</SectionHeader>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
              {SITE.cards.map((card) => (
                <PixelCard key={card.title} icon={card.icon} title={card.title} lines={card.lines} />
              ))}
            </div>
            <Dialog name="🍵 RYOKAN GUIDE">
              &quot;Welcome to Nanten-en. The koi pond is out back, garden ceremony
              at dusk, yukata provided. The staff will guide you. See you there!&quot;
              <span className="cursor">█</span>
            </Dialog>
          </div>
        </>
      )}

      {/* ════════════ STORY PAGE ════════════ */}
      {page === 'story' && (
        <div style={{ padding: '10px 12px', overflowY: 'auto', maxHeight: '62vh' }}>
          <SectionHeader>OUR CHAPTERS</SectionHeader>
          {TIMELINE.map((entry, i) => (
            <TimelineRow key={i} {...entry} />
          ))}
          <Dialog name="🌸 OUR STORY">
            &quot;Two worlds, two celebrations, one forever.&quot;
            <span className="cursor">█</span>
          </Dialog>
        </div>
      )}

      {/* ════════════ DETAILS PAGE ════════════ */}
      {page === 'details' && (
        <div style={{ padding: '10px 12px', overflowY: 'auto', maxHeight: '62vh' }}>
          <PixelWindow title="⛩ NANTEN-EN · DETAILS">
            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT }}>
              <thead>
                <tr>
                  {['DETAIL','INFO'].map((h) => (
                    <th
                      key={h}
                      style={{
                        fontSize: 7, padding: '6px 8px',
                        border: `2px solid ${COLOURS.borderGold}`,
                        background: '#f5dbb8', color: '#8a3a18', textAlign: 'left',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {SITE.details.map(([key, val], i) => (
                  <tr key={i}>
                    <td style={{ fontSize:7, padding:'6px 8px', border:`2px solid ${COLOURS.borderGold}`, color:COLOURS.textDark, background: i%2===0?COLOURS.cream:COLOURS.creamDark }}>{key}</td>
                    <td style={{ fontSize:7, padding:'6px 8px', border:`2px solid ${COLOURS.borderGold}`, color:COLOURS.primary, background: i%2===0?COLOURS.cream:COLOURS.creamDark }}>{val}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </PixelWindow>
          <SectionHeader>DRESS CODE</SectionHeader>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
            <PixelCard icon="🍂" title="COLOURS"   lines={['Burgundy · Sage', 'Rust · Warm Gold', 'Forest Green']} />
            <PixelCard icon="🌿" title="VIBE"      lines={['Enchanted garden', 'Autumn whimsy', 'Fairy elegance']} />
            <PixelCard icon="🧥" title="WEATHER"   lines={['~15–20°C evening', 'Bring a shawl', 'or wrap!']} />
            <PixelCard icon="👟" title="FOOTWEAR"  lines={['Garden paths', 'Block heels or', 'flats ideal']} />
          </div>
          <Dialog name="🗺 TRAVEL TIP">
            &quot;Bring cash, a Suica card, and warm layers. The ryokan provides
            yukata — wear them to breakfast. 10/10 cosy.&quot;
            <span className="cursor">█</span>
          </Dialog>
        </div>
      )}

      {/* ════════════ RSVP PAGE ════════════ */}
      {page === 'rsvp' && (
        <RsvpForm venue="⛩ OSAKA" accentColour={SITE.accentColour} />
      )}

      <BottomNav items={NAV_ITEMS.japan} active={page} onNav={handleNav} />
    </div>
  );
}

JapanSite.propTypes = {
  onBack: PropTypes.func.isRequired,
};

export default JapanSite;
